import {
  ANALYTICS_CONFIG_EVENT_NAME,
  DATA_LAYER_KEY,
  DEFAULT_CONSENT_SETTINGS,
  GTM_EVENT_NAME,
  GTM_SCRIPT_ID,
  TIKTOK_PIXEL_ID,
  TIKTOK_SCRIPT_ID,
} from '../constants/analytics';
import { useConsentStore } from '../store/consent';
import {
  CONSENT_EVENT_TYPE,
  CONSENT_SOURCE,
  type AnalyticsConfiguration,
  type ConsentEventType,
  type ConsentSettings,
} from '../types/analytics';

type DataLayerEvent = Record<string, unknown> | IArguments;

type ConsentCommandPayload = {
  ad_storage: ConsentSettings['adStorage'];
  ad_user_data: ConsentSettings['adUserData'];
  ad_personalization: ConsentSettings['adPersonalization'];
  analytics_storage: ConsentSettings['analyticsStorage'];
  functionality_storage: ConsentSettings['functionalityStorage'];
  personalization_storage: ConsentSettings['personalizationStorage'];
  security_storage: ConsentSettings['securityStorage'];
  wait_for_update?: ConsentSettings['waitForUpdate'];
  ads_data_redaction?: boolean;
};

type AnalyticsEventParams = Record<
  string,
  string | number | boolean | null | undefined
>;

type TiktokAnalyticsQueue = unknown[] & {
  methods?: string[];
  setAndDefer?: (queue: TiktokAnalyticsQueue, methodName: string) => void;
  instance?: (pixelId: string) => TiktokAnalyticsQueue;
  load?: (pixelId: string, options?: Record<string, unknown>) => void;
  page?: () => void;
  track?: (eventName: string, eventParams?: AnalyticsEventParams) => void;
  holdConsent?: () => void;
  revokeConsent?: () => void;
  grantConsent?: () => void;
};

const ANALYTICS_ONCE_KEY_PREFIX = 'analytics-once';
const DATA_LAYER_VALUE_PREFIX = 'dlv_';
const TIKTOK_ANALYTICS_OBJECT = 'ttq';
const TIKTOK_SCRIPT_URL = 'https://analytics.tiktok.com/i18n/pixel/events.js';

let isAnalyticsInitialized = false;
let unsubscribeFromConsentUpdates: (() => void) | undefined;

const isBrowser = () => typeof window !== 'undefined';

const getAnalyticsConfiguration = (): AnalyticsConfiguration => ({
  gtmId: import.meta.env.VITE_GTM_ID,
  isAnalyticsEnabled: import.meta.env.VITE_ANALYTICS_ENABLED !== 'false',
});

const ensureDataLayer = (): DataLayerEvent[] => {
  const windowWithDataLayer = window as Window & {
    [DATA_LAYER_KEY]?: DataLayerEvent[];
  };

  windowWithDataLayer[DATA_LAYER_KEY] = windowWithDataLayer[DATA_LAYER_KEY] || [];
  return windowWithDataLayer[DATA_LAYER_KEY];
};

const pushDataLayerEvent = (event: DataLayerEvent) => {
  const dataLayer = ensureDataLayer();
  dataLayer.push(event);
};

const prefixAnalyticsEventParams = (
  eventParams: AnalyticsEventParams | undefined,
): AnalyticsEventParams => {
  if (!eventParams) {
    return {};
  }

  return Object.entries(eventParams).reduce<AnalyticsEventParams>(
    (prefixedEventParams, [paramName, paramValue]) => {
      const prefixedParamName = paramName.startsWith(DATA_LAYER_VALUE_PREFIX)
        ? paramName
        : `${DATA_LAYER_VALUE_PREFIX}${paramName}`;

      return {
        ...prefixedEventParams,
        [prefixedParamName]: paramValue,
      };
    },
    {},
  );
};

export const trackAnalyticsEvent = (
  eventName: string,
  eventParams?: AnalyticsEventParams,
) => {
  if (!isBrowser()) {
    return;
  }

  const analyticsConfiguration = getAnalyticsConfiguration();
  if (!analyticsConfiguration.isAnalyticsEnabled) {
    return;
  }

  pushDataLayerEvent({
    event: `${eventName}`,
    ...prefixAnalyticsEventParams(eventParams),
  });
};

export const trackPageView = (pageData: {
  page_path: string;
  page_location: string;
  page_title?: string;
}) => {
  if (!isBrowser()) return;

  const config = getAnalyticsConfiguration();
  if (!config.isAnalyticsEnabled) return;

  pushDataLayerEvent({
    event: 'page_view',
    page_path: pageData.page_path,
    page_location: pageData.page_location,
    page_title: pageData.page_title || document.title,
  });

  getTiktokQueue()?.page?.();
};

export const trackAnalyticsEventOnce = (
  eventName: string,
  uniqueKey: string,
  eventParams?: AnalyticsEventParams,
) => {
  if (!isBrowser()) {
    return false;
  }

  const onceStorageKey = `${ANALYTICS_ONCE_KEY_PREFIX}:${eventName}:${uniqueKey}`;

  if (sessionStorage.getItem(onceStorageKey) === '1') {
    return false;
  }

  trackAnalyticsEvent(eventName, eventParams);
  sessionStorage.setItem(onceStorageKey, '1');

  return true;
};

const getConsentCommandPayload = (
  consentSettings: ConsentSettings,
  event: ConsentEventType,
): ConsentCommandPayload => ({
  ad_storage: consentSettings.adStorage,
  ad_user_data: consentSettings.adUserData,
  ad_personalization: consentSettings.adPersonalization,
  analytics_storage: consentSettings.analyticsStorage,
  functionality_storage: consentSettings.functionalityStorage,
  personalization_storage: consentSettings.personalizationStorage,
  security_storage: consentSettings.securityStorage,
  ads_data_redaction: true,
  ...(event === CONSENT_EVENT_TYPE.DEFAULT
    ? { wait_for_update: consentSettings.waitForUpdate }
    : {}),
});

const ensureGtag = () => {
  const windowWithGtag = window as Window & {
    gtag?: (
      command: 'consent',
      action: ConsentEventType,
      payload: ConsentCommandPayload,
    ) => void;
  };

  if (!windowWithGtag.gtag) {
    windowWithGtag.gtag = function gtagConsentShim() {
      const dataLayer = ensureDataLayer();
      dataLayer.push(arguments);
    };
  }

  return windowWithGtag.gtag;
};

const sendConsentToGtm = (
  consentSettings: ConsentSettings,
  event: ConsentEventType,
) => {
  const gtag = ensureGtag();
  gtag('consent', event, getConsentCommandPayload(consentSettings, event));
};

const appendGtmScript = (gtmId: string) => {
  if (document.getElementById(GTM_SCRIPT_ID)) {
    return;
  }

  const gtmScript = document.createElement('script');
  gtmScript.id = GTM_SCRIPT_ID;
  gtmScript.async = true;
  gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
  document.head.appendChild(gtmScript);
};

const getTiktokQueue = () => {
  const windowWithTiktok = window as Window & {
    TiktokAnalyticsObject?: string;
    ttq?: TiktokAnalyticsQueue;
  };

  return windowWithTiktok.ttq;
};

const ensureTiktokQueue = () => {
  const windowWithTiktok = window as Window & {
    TiktokAnalyticsObject?: string;
    ttq?: TiktokAnalyticsQueue;
  };

  windowWithTiktok.TiktokAnalyticsObject = TIKTOK_ANALYTICS_OBJECT;
  const ttq = windowWithTiktok.ttq || ([] as unknown as TiktokAnalyticsQueue);
  windowWithTiktok.ttq = ttq;

  ttq.methods = [
    'page',
    'track',
    'identify',
    'instances',
    'debug',
    'on',
    'off',
    'once',
    'ready',
    'alias',
    'group',
    'enableCookie',
    'disableCookie',
    'holdConsent',
    'revokeConsent',
    'grantConsent',
  ];

  ttq.setAndDefer = (queue, methodName) => {
    (queue as TiktokAnalyticsQueue & Record<string, unknown>)[methodName] =
      function deferTiktokCall(...args: unknown[]) {
        queue.push([methodName, ...args]);
      };
  };

  for (const methodName of ttq.methods) {
    ttq.setAndDefer(ttq, methodName);
  }

  ttq.instance = (pixelId) => {
    const queueWithInstances = ttq as TiktokAnalyticsQueue & {
      _i?: Record<string, TiktokAnalyticsQueue>;
    };
    const instanceQueue = queueWithInstances._i?.[pixelId] || [];

    for (const methodName of ttq.methods || []) {
      ttq.setAndDefer?.(instanceQueue as TiktokAnalyticsQueue, methodName);
    }

    return instanceQueue as TiktokAnalyticsQueue;
  };

  ttq.load = (pixelId, options) => {
    const queueWithMetadata = ttq as TiktokAnalyticsQueue & {
      _i?: Record<string, TiktokAnalyticsQueue & { _u?: string }>;
      _t?: Record<string, number>;
      _o?: Record<string, Record<string, unknown> | undefined>;
    };

    queueWithMetadata._i = queueWithMetadata._i || {};
    queueWithMetadata._i[pixelId] = [];
    queueWithMetadata._i[pixelId]._u = TIKTOK_SCRIPT_URL;
    queueWithMetadata._t = queueWithMetadata._t || {};
    queueWithMetadata._t[pixelId] = Date.now();
    queueWithMetadata._o = queueWithMetadata._o || {};
    queueWithMetadata._o[pixelId] = options;

    if (document.getElementById(TIKTOK_SCRIPT_ID)) {
      return;
    }

    const tiktokScript = document.createElement('script');
    tiktokScript.id = TIKTOK_SCRIPT_ID;
    tiktokScript.type = 'text/javascript';
    tiktokScript.async = true;
    tiktokScript.src = `${TIKTOK_SCRIPT_URL}?sdkid=${encodeURIComponent(pixelId)}&lib=${TIKTOK_ANALYTICS_OBJECT}`;

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode?.insertBefore(tiktokScript, firstScript);
  };

  return ttq;
};

const sendConsentToTiktok = (consentSettings: ConsentSettings) => {
  const ttq = getTiktokQueue();
  if (!ttq) {
    return;
  }

  if (consentSettings.adStorage === 'granted') {
    ttq.grantConsent?.();
  } else {
    ttq.revokeConsent?.();
  }
};

const bootstrapTiktok = (consentSettings: ConsentSettings) => {
  const ttq = ensureTiktokQueue();

  if (consentSettings.adStorage === 'granted') {
    ttq.grantConsent?.();
  } else {
    ttq.holdConsent?.();
  }

  ttq.load?.(TIKTOK_PIXEL_ID);
  ttq.page?.();
};

const bootstrapGtm = (gtmId: string) => {
  pushDataLayerEvent({
    'gtm.start': Date.now(),
    event: GTM_EVENT_NAME,
  });

  appendGtmScript(gtmId);
};

const startConsentSubscription = () => {
  if (unsubscribeFromConsentUpdates) {
    return;
  }

  unsubscribeFromConsentUpdates = useConsentStore.subscribe(
    (nextState, previousState) => {
      if (nextState.consent === previousState.consent) {
        return;
      }

      sendConsentToGtm(
        nextState.consent,
        CONSENT_EVENT_TYPE.UPDATE,
      );
      sendConsentToTiktok(nextState.consent);
    },
  );
};

export const initializeAnalytics = () => {
  if (!isBrowser() || isAnalyticsInitialized) {
    return;
  }

  const analyticsConfiguration = getAnalyticsConfiguration();
  if (!analyticsConfiguration.isAnalyticsEnabled) {
    console.info('Analytics disabled by environment configuration.');
    return;
  }

  const { consent, source } = useConsentStore.getState().initializeConsent();

  if (source === CONSENT_SOURCE.STORED) {
    sendConsentToGtm(DEFAULT_CONSENT_SETTINGS, CONSENT_EVENT_TYPE.DEFAULT);
    sendConsentToGtm(consent, CONSENT_EVENT_TYPE.UPDATE);
  } else {
    sendConsentToGtm(consent, CONSENT_EVENT_TYPE.DEFAULT);
  }

  bootstrapTiktok(consent);

  pushDataLayerEvent({
    event: ANALYTICS_CONFIG_EVENT_NAME,
    gtm_id: analyticsConfiguration.gtmId,
  });

  if (analyticsConfiguration.gtmId) {
    bootstrapGtm(analyticsConfiguration.gtmId);
  } else {
    console.warn('VITE_GTM_ID is missing. GTM initialization skipped.');
  }
  startConsentSubscription();

  isAnalyticsInitialized = true;
};
